import { faGripVertical, faPlus, faSkullCrossbones, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Form, InputGroup, Row, ToggleButton } from "react-bootstrap";
import { 
    DndContext, 
    DragEndEvent, 
    DragOverEvent, 
    DragMoveEvent,
    closestCenter, 
    pointerWithin,
    rectIntersection,
    PointerSensor, 
    useSensor, 
    useSensors, 
    DragStartEvent,
    CollisionDetection,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DEFAULT_ATHLETE_NAMES } from "../common/constants";
import { getMajorityTime } from "../common/util";
import { Athlete, State } from "../types";
import { CopyButton } from "./CopyButton";
import { useDroppable } from '@dnd-kit/core';
import { useState, useRef, useCallback } from 'react';

interface Props {
    athletes: Athlete[];
    onChange: (athletes: Athlete[]) => void;
    state: State;
    currentAthleteId?: string;
    onPlusTimeAll?: (seconds: number) => void;
}

interface AthleteRowProps {
    athlete: Athlete;
    athleteIndex: number;
    state: State;
    onChange: (athletes: Athlete[]) => void;
    getBackgroundColor: (index: number) => string;
    allAthletes: Athlete[];
}

const AthleteRow = ({
    athlete,
    athleteIndex,
    state,
    onChange,
    getBackgroundColor,
    allAthletes
}: AthleteRowProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        isDragging
    } = useSortable({ 
        id: `athlete-${athleteIndex}`,
        disabled: state !== "running" && state !== "paused" // Only enable dragging when running or paused
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        background: isDragging ? '#f8f9fa' : 'transparent',
        marginBottom: '1rem',
        touchAction: 'none',
        position: 'relative' as const,
        zIndex: isDragging ? 1 : 0,
        userSelect: 'none' as const
    };

    return (
        <div ref={setNodeRef} style={style}>
            <Form.Group as={Row} controlId={`athlete-${athleteIndex}`}>
                <Col sm={12}>
                    <InputGroup>
                        {state !== "stopped" && (
                            <div 
                                className="input-group-text"
                                style={{ 
                                    cursor: 'grab',
                                    touchAction: 'none',
                                    userSelect: 'none',
                                }}
                                {...attributes}
                                {...listeners}
                            >
                                <FontAwesomeIcon icon={faGripVertical} />
                            </div>
                        )}
                        <Form.Control
                            style={{ background: getBackgroundColor(athleteIndex) }}
                            type="text"
                            placeholder="Name"
                            value={athlete.text}
                            onChange={(e) =>
                                onChange(
                                    allAthletes.map((a, ai) => (ai === athleteIndex ? { ...a, text: e.target.value } : a))
                                )
                            }
                        />
                        <Form.Control
                            style={{ background: getBackgroundColor(athleteIndex) }}
                            type="number"
                            placeholder="Time (in seconds)"
                            min={5}
                            step={5}
                            value={athlete.time}
                            onChange={(e) =>
                                onChange(
                                    allAthletes.map((a, ai) => (ai === athleteIndex ? { ...a, time: +e.target.value } : a))
                                )
                            }
                        />
                        {state === "stopped" ? (
                            <Button
                                variant="danger"
                                disabled={allAthletes.length === 1}
                                onClick={() => onChange(allAthletes.filter((_, ai) => ai !== athleteIndex))}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        ) : (
                            <ToggleButton
                                id={`athlete-${athleteIndex}-enabled`}
                                variant="outline-secondary"
                                type="checkbox"
                                name={`athlete-${athleteIndex}-enabled`}
                                checked={!athlete.enabled}
                                disabled={athlete.enabled && allAthletes.filter((a) => a.enabled).length === 1}
                                value={athleteIndex}
                                onChange={(e) =>
                                    onChange(
                                        allAthletes.map((a, ai) =>
                                            ai === athleteIndex ? { ...a, enabled: !e.target.checked } : a
                                        )
                                    )
                                }
                            >
                                <FontAwesomeIcon icon={faSkullCrossbones} />
                            </ToggleButton>
                        )}
                    </InputGroup>
                </Col>
            </Form.Group>
        </div>
    );
};

export function AthletesSettings({ athletes, onChange, state, currentAthleteId, onPlusTimeAll }: Props) {
    const [isDragging, setIsDragging] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [isOverDeleteZone, setIsOverDeleteZone] = useState(false);
    const isOverDeleteZoneRef = useRef(false); // Ref for synchronous access in handlers
    const deleteZoneRef = useRef<HTMLDivElement | null>(null); // Ref to the actual delete zone element
    
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
                tolerance: 5,
                delay: 0
            },
        })
    );

    // Custom collision detection that prioritizes the delete zone
    const customCollisionDetection: CollisionDetection = useCallback((args) => {
        // First check if we're over the delete zone using rectIntersection
        const rectCollisions = rectIntersection(args);
        const deleteZoneCollision = rectCollisions.find(
            (collision) => collision.id === 'delete-zone'
        );
        
        if (deleteZoneCollision) {
            return [deleteZoneCollision];
        }
        
        // Also try pointerWithin as a fallback
        const pointerCollisions = pointerWithin(args);
        const deleteZonePointerCollision = pointerCollisions.find(
            (collision) => collision.id === 'delete-zone'
        );
        
        if (deleteZonePointerCollision) {
            return [deleteZonePointerCollision];
        }
        
        // Otherwise, use closestCenter for the sortable items
        return closestCenter(args);
    }, []);

    const { setNodeRef: setDeleteDropRef } = useDroppable({
        id: 'delete-zone',
    });

    const discordCommand = `!t config athletes ${athletes
        .map((athlete) => `${athlete.text.trim().replaceAll(" ", "_").replaceAll(" ", "_")}:${athlete.time}`)
        .join(" ")}`;

    const getBackgroundColor = (athleteIndex: number) => {
        if (state === "stopped") {
            return "#fff";
        }

        if (!athletes[athleteIndex].enabled) {
            return "#6c757d80";
        }

        return "#fff";
    };

    // Helper function to check if a point is within the delete zone
    const isPointInDeleteZone = useCallback((x: number, y: number): boolean => {
        if (!deleteZoneRef.current) return false;
        const rect = deleteZoneRef.current.getBoundingClientRect();
        return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    }, []);

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        setActiveId(active.id as string);
        setIsDragging(true);
        setIsOverDeleteZone(false);
        isOverDeleteZoneRef.current = false;
    };

    const handleDragMove = (event: DragMoveEvent) => {
        // Get the pointer position from the event
        const { activatorEvent, delta } = event;
        if (activatorEvent && 'clientX' in activatorEvent && 'clientY' in activatorEvent) {
            const x = (activatorEvent as MouseEvent).clientX + (delta?.x || 0);
            const y = (activatorEvent as MouseEvent).clientY + (delta?.y || 0);
            const isOver = isPointInDeleteZone(x, y);
            setIsOverDeleteZone(isOver);
            isOverDeleteZoneRef.current = isOver;
        }
    };

    const handleDragOver = (event: DragOverEvent) => {
        const { over } = event;
        const isOver = over?.id === 'delete-zone';
        // Don't override the manual check from handleDragMove if we detected the delete zone
        if (!isOverDeleteZoneRef.current) {
            setIsOverDeleteZone(isOver);
            isOverDeleteZoneRef.current = isOver;
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        
        // Check both the ref (last known state from onDragOver) and the current over value
        const wasOverDeleteZone = isOverDeleteZoneRef.current;
        const isCurrentlyOverDelete = over?.id === 'delete-zone';
        const shouldDelete = wasOverDeleteZone || isCurrentlyOverDelete || isOverDeleteZone;
        
        setActiveId(null);
        setIsDragging(false);
        setIsOverDeleteZone(false);
        isOverDeleteZoneRef.current = false;
        
        // Handle deletion if we were over the delete zone
        if (shouldDelete) {
            const athleteIndex = parseInt(active.id.toString().split('-')[1]);
            // Don't allow deleting if it's the last enabled athlete
            const enabledAthletes = athletes.filter((a) => a.enabled);
            if (enabledAthletes.length === 1 && athletes[athleteIndex].enabled) {
                return;
            }
            onChange(athletes.filter((_, ai) => ai !== athleteIndex));
            return;
        }
        
        if (!over) return;
        if (active.id === over.id) return;

        const oldIndex = parseInt(active.id.toString().split('-')[1]);
        const newIndex = parseInt(over.id.toString().split('-')[1]);
        
        onChange(arrayMove(athletes, oldIndex, newIndex));
    };

    return (
        <>
            <DndContext
                sensors={sensors}
                collisionDetection={customCollisionDetection}
                onDragStart={handleDragStart}
                onDragMove={handleDragMove}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={athletes.map((_, i) => `athlete-${i}`)}
                    strategy={verticalListSortingStrategy}
                >
                    {athletes.map((athlete, index) => (
                        <AthleteRow
                            key={`athlete-${index}`}
                            athlete={athlete}
                            athleteIndex={index}
                            state={state}
                            onChange={onChange}
                            getBackgroundColor={getBackgroundColor}
                            allAthletes={athletes}
                        />
                    ))}
                </SortableContext>

                <div 
                    ref={(node) => {
                        // Set both refs
                        setDeleteDropRef(node);
                        deleteZoneRef.current = node;
                    }}
                    className="d-grid mt-3"
                    style={{
                        transition: 'all 0.2s ease-in-out',
                        transform: isOverDeleteZone ? 'scale(1.05)' : 'scale(1)',
                        padding: '10px',
                        border: isOverDeleteZone ? '3px dashed #dc3545' : (isDragging ? '2px dashed #6c757d' : '2px dashed transparent'),
                        borderRadius: '8px',
                        backgroundColor: isOverDeleteZone ? 'rgba(220, 53, 69, 0.1)' : 'transparent',
                    }}
                >
                    {isDragging ? (
                        <Button
                            variant={isOverDeleteZone ? "danger" : "outline-danger"}
                            className="w-100"
                            style={{ 
                                height: '48px',
                                fontSize: '1.1rem',
                                backgroundColor: isOverDeleteZone ? undefined : 'rgba(220, 53, 69, 0.15)',
                                borderColor: '#dc3545',
                            }}
                        >
                            <FontAwesomeIcon icon={faTrash} /> Remove athlete
                        </Button>
                    ) : state !== "stopped" && onPlusTimeAll ? (
                        <div className="btn-group justify-content-center" role="group" aria-label="All Athletes Time Control">
                            <button type="button" className="btn btn-secondary toolbar-btn-15" onClick={() => onPlusTimeAll(-15)}>-15</button>
                            <button type="button" className="btn btn-secondary toolbar-btn-10" onClick={() => onPlusTimeAll(-10)}>-10</button>
                            <button type="button" className="btn btn-secondary" onClick={() => onPlusTimeAll(-5)}>-5</button>
                            <Button
                                variant="light"
                                onClick={() => {
                                    onChange([
                                        ...athletes,
                                        {
                                            id: crypto.randomUUID(),
                                            text: DEFAULT_ATHLETE_NAMES[athletes.length] ?? "",
                                            time: getMajorityTime(athletes),
                                            enabled: true,
                                        },
                                    ]);
                                }}
                                style={{ 
                                    height: '38px',
                                    padding: '6px 12px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <FontAwesomeIcon icon={faPlus} /> 
                                <span className="btn-text-responsive">Add athlete</span>
                            </Button>
                            <button type="button" className="btn btn-secondary" onClick={() => onPlusTimeAll(5)}>+5</button>
                            <button type="button" className="btn btn-secondary toolbar-btn-10" onClick={() => onPlusTimeAll(10)}>+10</button>
                            <button type="button" className="btn btn-secondary toolbar-btn-15" onClick={() => onPlusTimeAll(15)}>+15</button>
                        </div>
                    ) : (
                        <Button
                            variant="light"
                            onClick={() => {
                                onChange([
                                    ...athletes,
                                    {
                                        id: crypto.randomUUID(),
                                        text: DEFAULT_ATHLETE_NAMES[athletes.length] ?? "",
                                        time: getMajorityTime(athletes),
                                        enabled: true,
                                    },
                                ]);
                            }}
                            style={{ 
                                height: '38px',
                                padding: '6px 12px',
                            }}
                        >
                            <FontAwesomeIcon icon={faPlus} /> Add athlete
                        </Button>
                    )}
                </div>
            </DndContext>
        </>
    );
}